import React, { useState } from "react";
import { OWGameResponse } from "@devour/client";
import { FaGamepad, FaCalendar, FaCode, FaGlobe } from "react-icons/fa";
import ScreenshotLightbox from "./ScreenshotLightbox";

interface GameAboutSectionProps {
    game: OWGameResponse;
}

const GameAboutSection: React.FC<GameAboutSectionProps> = ({ game }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // Extract game details from steamData
    const steamData = (game as any).steamData || {};
    const genres = steamData.genres?.join(", ") || "Action";
    const developers = steamData.developers?.join(", ") || game.publisher || "Unknown Developer";
    const publishers = steamData.publishers?.join(", ") || game.publisher || "Unknown Publisher";
    const releaseDate = steamData.releaseDate || steamData.release_date?.date || "TBA";
    const categories = steamData.categories || [];
    const screenshots = steamData.screenshots || [];

    const handleScreenshotClick = (index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    // Parse release date if it's a date string
    const formattedReleaseDate = releaseDate.includes("T")
        ? new Date(releaseDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
        : releaseDate;

    return (
        <div className="game-about-section">
            <div className="game-about-section__header">
                <h2>About {game.name}</h2>
            </div>

            <div className="game-about-section__content">
                <div className="game-about-section__description">
                    <p>
                        {game.description || steamData.shortDescription || steamData.description ||
                         `${game.name} is an exciting gaming experience available on DevourPlay. ` +
                         "Join thousands of players earning rewards and competing in challenges."}
                    </p>
                    {steamData.shortDescription && game.description && steamData.shortDescription !== game.description &&
                        <p>{steamData.shortDescription}</p>
                    }
                </div>

                <div className="game-about-section__details">
                    <h3>Game Details</h3>
                    <div className="game-about-section__detail-grid">
                        <div className="game-about-section__detail">
                            <FaGamepad className="game-about-section__detail-icon" />
                            <div>
                                <span className="game-about-section__detail-label">Genre</span>
                                <span className="game-about-section__detail-value">{genres}</span>
                            </div>
                        </div>
                        <div className="game-about-section__detail">
                            <FaCalendar className="game-about-section__detail-icon" />
                            <div>
                                <span className="game-about-section__detail-label">Release Date</span>
                                <span className="game-about-section__detail-value">{formattedReleaseDate}</span>
                            </div>
                        </div>
                        <div className="game-about-section__detail">
                            <FaCode className="game-about-section__detail-icon" />
                            <div>
                                <span className="game-about-section__detail-label">Developer</span>
                                <span className="game-about-section__detail-value">{developers}</span>
                            </div>
                        </div>
                        <div className="game-about-section__detail">
                            <FaGlobe className="game-about-section__detail-icon" />
                            <div>
                                <span className="game-about-section__detail-label">Publisher</span>
                                <span className="game-about-section__detail-value">{publishers}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {screenshots.length > 0 &&
                    <div className="game-about-section__screenshots">
                        <h3>Screenshots</h3>
                        <div className="game-about-section__screenshot-grid">
                            {screenshots.slice(0, 4).map((screenshot, index) =>
                                <div
                                    key={index}
                                    className="game-about-section__screenshot game-about-section__screenshot--interactive"
                                    onClick={() => handleScreenshotClick(index)}
                                >
                                    <img
                                        src={screenshot}
                                        alt={`${game.name} screenshot ${index + 1}`}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                        }}
                                    />
                                </div>)}
                        </div>
                    </div>
                }

                {categories.length > 0 &&
                    <div className="game-about-section__tags">
                        <h3>Tags</h3>
                        <div className="game-about-section__tag-list">
                            {categories.slice(0, 12).map(tag =>
                                <span key={tag} className="game-about-section__tag">{tag}</span>)}
                        </div>
                    </div>
                }
            </div>

            <ScreenshotLightbox
                isOpen={lightboxOpen}
                screenshots={screenshots}
                initialIndex={lightboxIndex}
                onClose={() => setLightboxOpen(false)}
            />
        </div>
    );
};

export default GameAboutSection;